import { PricingUnit, PricePrecision, CostDuration } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import {
    regex,
    makeCellWithRegexSorter,
    expr,
    transformAllDataTables,
    resolvePricePrecision,
} from "./shared";
import exprCompiler from "@/utils/expr";

// Net (excl. VAT) EUR prices per Hetzner location: Hetzner Cloud server types
// and dedicated root servers, as scraped by scraper/hetzner.
export interface HetznerPricing {
    [location: string]: {
        hourly: string;
        monthly: string;
        setup: string; // one-off; "0" for cloud
        traffic: string; // "20 TB" or "Unlimited"
    };
}

export type HetznerInstance = {
    instance_type: string;
    pretty_name: string;
    type: "cloud" | "dedicated";
    vCPU: number;
    memory: number;
    disk: number; // total GB
    disk_detail: string;
    cpu: string; // model; empty for cloud
    gpu: string;
    cpu_type: string;
    architecture: string;
    category: string;
    deprecated: boolean;
    pricing: HetznerPricing;
    regions: { [location: string]: string };
};

type Currency = {
    code: string;
    usdRate: number;
    cnyRate: number;
    eurRate: number;
};

const initialColumnsArr = [
    ["pretty_name", true],
    ["instance_type", true],
    ["type", true],
    ["memory", true],
    ["vCPU", true],
    ["memory_per_vcpu", false],
    ["disk", true],
    ["cpu", true],
    ["gpu", false],
    ["cpu_type", false],
    ["architecture", true],
    ["traffic", false],
    ["deprecated", false],
    ["ondemand", true],
    ["monthly", true],
    ["setup", true],
] as const;

export const initialColumnsValue: {
    [idx in (typeof initialColumnsArr)[number][0]]: boolean;
} = {} as any;
for (const [key, value] of initialColumnsArr) {
    initialColumnsValue[key] = value;
}

export function transformDataTables(dataTablesData: any) {
    return transformAllDataTables(initialColumnsArr, dataTablesData);
}

export function makePrettyNames<V>(
    makeColumnOption: (
        key: keyof typeof initialColumnsValue,
        label: string,
    ) => V,
    _reservedTerm: string,
) {
    return [
        makeColumnOption("pretty_name", "Server Name"),
        makeColumnOption("instance_type", "API Name"),
        makeColumnOption("type", "Type"),
        makeColumnOption("memory", "Memory"),
        makeColumnOption("vCPU", "vCPUs / Threads"),
        makeColumnOption("memory_per_vcpu", "Memory per vCPU"),
        makeColumnOption("disk", "Storage"),
        makeColumnOption("cpu", "CPU"),
        makeColumnOption("gpu", "GPU"),
        makeColumnOption("cpu_type", "CPU Type"),
        makeColumnOption("architecture", "Architecture"),
        makeColumnOption("traffic", "Included Traffic"),
        makeColumnOption("deprecated", "Deprecated"),
        makeColumnOption("ondemand", "Hourly Price"),
        makeColumnOption("monthly", "Monthly Price"),
        makeColumnOption("setup", "Setup Fee"),
    ];
}

function round(value: number) {
    return Math.round(value * 100) / 100;
}

const hourMultipliers: Record<CostDuration, number> = {
    secondly: 1 / (60 * 60),
    minutely: 1 / 60,
    hourly: 1,
    daily: 24,
    weekly: 7 * 24,
    monthly: (365 * 24) / 12,
    annually: 365 * 24,
};

function calculateCost(
    price: string | undefined,
    instance: HetznerInstance,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    eurRate: number,
): number {
    if (!price) return -1;
    let pricingUnitModifier = 1;
    if (pricingUnit !== "instance") {
        pricingUnitModifier = instance[
            pricingUnit === "memory" ? "memory" : "vCPU"
        ] as number;
    }
    return (
        ((Number(price) * hourMultipliers[costDuration]) /
            pricingUnitModifier) *
        eurRate
    );
}

function formatMoney(value: number, code: string, precision: number) {
    return Intl.NumberFormat("en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
    }).format(value);
}

export function calculateAndFormatCost(
    price: string | undefined,
    instance: HetznerInstance,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    currency: Currency,
    pricePrecision: PricePrecision = "auto",
): string | undefined {
    const perTime = calculateCost(
        price,
        instance,
        pricingUnit,
        costDuration,
        currency.eurRate,
    );
    if (perTime === -1) return undefined;

    const precision = resolvePricePrecision(pricePrecision, costDuration);
    let durationText: string = costDuration;
    if (costDuration === "secondly") durationText = "per sec";
    if (costDuration === "minutely") durationText = "per min";
    const unitText =
        pricingUnit === "instance"
            ? ""
            : ` / ${pricingUnit === "memory" ? "GiB" : "vCPU"}`;

    return `${formatMoney(perTime, currency.code, precision)} ${durationText}${unitText}`;
}

function yesNo(accessor: (row: HetznerInstance) => boolean) {
    return {
        filterFn: (row, _, filterValue) => {
            const value = accessor(row.original);
            try {
                return exprCompiler(filterValue)(
                    value ? 1 : 0,
                    value ? "Yes" : "No",
                );
            } catch {
                return true;
            }
        },
        cell: (info) => (accessor(info.row.original) ? "Yes" : "No"),
    } satisfies Partial<ColumnDef<HetznerInstance>>;
}

export const columnsGen = (
    selectedRegion: string,
    pricingUnit: PricingUnit,
    costDuration: CostDuration,
    pricePrecision: PricePrecision,
    _reservedTerm: string, // Hetzner has no reserved terms; kept for signature parity.
    currency: Currency,
): ColumnDef<HetznerInstance>[] => {
    const regionPricing = (row: HetznerInstance) =>
        row.pricing?.[selectedRegion];

    return [
        {
            accessorKey: "pretty_name",
            id: "pretty_name",
            header: "Server Name",
            sortingFn: "alphanumeric",
        },
        {
            accessorKey: "instance_type",
            id: "instance_type",
            header: "API Name",
            sortingFn: "alphanumeric",
            filterFn: regex({ accessorKey: "instance_type" }),
        },
        {
            accessorKey: "type",
            header: "Type",
            size: 110,
            id: "type",
            sortingFn: "alphanumeric",
            filterFn: regex({ accessorKey: "type" }),
            cell: (info) =>
                info.getValue() === "dedicated" ? "Dedicated" : "Cloud",
        },
        {
            accessorKey: "memory",
            header: "Memory",
            size: 160,
            id: "memory",
            sortingFn: "alphanumeric",
            filterFn: expr,
            cell: (info) => `${info.getValue() as number} GiB`,
        },
        {
            accessorKey: "vCPU",
            header: "vCPUs / Threads",
            size: 140,
            id: "vCPU",
            sortingFn: "alphanumeric",
            filterFn: expr,
        },
        {
            accessorKey: "memory",
            header: "Memory per vCPU",
            size: 160,
            id: "memory_per_vcpu",
            filterFn: (row, _, filterValue) => {
                const v = round(row.original.memory / row.original.vCPU);
                try {
                    return exprCompiler(filterValue)(v, `${v} GiB/vCPU`);
                } catch {
                    return true;
                }
            },
            cell: (info) =>
                `${round((info.getValue() as number) / info.row.original.vCPU)} GiB/vCPU`,
        },
        {
            accessorKey: "disk",
            header: "Storage",
            size: 220,
            id: "disk",
            sortingFn: "basic",
            filterFn: expr,
            cell: (info) => info.row.original.disk_detail,
        },
        {
            accessorKey: "cpu",
            header: "CPU",
            size: 200,
            id: "cpu",
            sortingFn: "alphanumeric",
            filterFn: regex({ accessorKey: "cpu" }),
        },
        {
            accessorKey: "gpu",
            header: "GPU",
            size: 200,
            id: "gpu",
            sortingFn: "alphanumeric",
            filterFn: regex({ accessorKey: "gpu" }),
        },
        {
            accessorKey: "cpu_type",
            header: "CPU Type",
            size: 120,
            id: "cpu_type",
            sortingFn: "alphanumeric",
            filterFn: regex({ accessorKey: "cpu_type" }),
        },
        {
            accessorKey: "architecture",
            header: "Architecture",
            size: 120,
            id: "architecture",
            sortingFn: "alphanumeric",
            filterFn: regex({ accessorKey: "architecture" }),
        },
        {
            accessorKey: "pricing",
            header: "Included Traffic",
            size: 140,
            id: "traffic",
            accessorFn: (row) => regionPricing(row)?.traffic,
            sortUndefined: "last",
            filterFn: (row, _, filterValue) => {
                const v = regionPricing(row.original)?.traffic;
                if (v === undefined) return false;
                try {
                    return new RegExp(filterValue, "i").test(v);
                } catch {
                    return true;
                }
            },
        },
        {
            accessorKey: "deprecated",
            header: "Deprecated",
            size: 120,
            id: "deprecated",
            ...yesNo((row) => row.deprecated),
        },
        {
            accessorKey: "pricing",
            header: "Hourly Price (excl. VAT)",
            id: "ondemand",
            sortUndefined: "last",
            accessorFn: (row) => {
                const v = calculateCost(
                    regionPricing(row)?.hourly,
                    row,
                    pricingUnit,
                    costDuration,
                    currency.eurRate,
                );
                return v === -1 ? undefined : v;
            },
            sortingFn: (a, b) =>
                ((a.getValue("ondemand") as number) ?? Infinity) -
                ((b.getValue("ondemand") as number) ?? Infinity),
            ...makeCellWithRegexSorter("pricing", (info) =>
                calculateAndFormatCost(
                    regionPricing(info.row.original)?.hourly,
                    info.row.original,
                    pricingUnit,
                    costDuration,
                    currency,
                    pricePrecision,
                ),
            ),
        },
        {
            accessorKey: "pricing",
            header: "Monthly Price (excl. VAT)",
            id: "monthly",
            sortUndefined: "last",
            accessorFn: (row) => {
                const m = regionPricing(row)?.monthly;
                return m ? Number(m) * currency.eurRate : undefined;
            },
            sortingFn: (a, b) =>
                ((a.getValue("monthly") as number) ?? Infinity) -
                ((b.getValue("monthly") as number) ?? Infinity),
            ...makeCellWithRegexSorter("pricing", (info) => {
                const v = info.getValue() as number | undefined;
                return v === undefined
                    ? undefined
                    : `${formatMoney(v, currency.code, 2)} monthly`;
            }),
        },
        {
            accessorKey: "pricing",
            header: "Setup Fee (excl. VAT)",
            id: "setup",
            sortUndefined: "last",
            // One-off, so it is converted to the chosen currency but not
            // scaled by the duration or pricing unit.
            accessorFn: (row) => {
                const v = regionPricing(row)?.setup;
                return v === undefined
                    ? undefined
                    : Number(v) * currency.eurRate;
            },
            sortingFn: (a, b) =>
                ((a.getValue("setup") as number) ?? Infinity) -
                ((b.getValue("setup") as number) ?? Infinity),
            ...makeCellWithRegexSorter("pricing", (info) => {
                const v = info.getValue() as number | undefined;
                return v === undefined
                    ? undefined
                    : `${formatMoney(v, currency.code, 2)} once`;
            }),
        },
    ];
};
